<template>
  <div class="gl-categories-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-branch-header">
        <div>
          <div class="gl-branch-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Categories</span>
          </div>
          <h1 class="gl-branch-title">Categories</h1>
        </div>
        <router-link to="/owner/categories/create" class="gl-add-btn gl-header-add-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Category
        </router-link>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Stats strip -->
      <div class="gl-stats-strip">
        <div class="gl-stat-chip">
          <span class="gl-stat-num">{{ categories.length }}</span>
          <span class="gl-stat-lbl">Total</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num gl-stat-active">{{ categories.filter(c => c.is_active).length }}</span>
          <span class="gl-stat-lbl">Active</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num gl-stat-inactive">{{ categories.filter(c => !c.is_active).length }}</span>
          <span class="gl-stat-lbl">Inactive</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num" style="color:#6b7280">{{ totalProducts }}</span>
          <span class="gl-stat-lbl">Products</span>
        </div>
        <div class="gl-stat-spacer"></div>
        <div class="gl-view-toggle">
          <button :class="['gl-vt-btn', { active: viewMode === 'cards' }]" @click="viewMode = 'cards'" title="Card view">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="2" width="9" height="9" rx="2"/><rect x="13" y="2" width="9" height="9" rx="2"/><rect x="2" y="13" width="9" height="9" rx="2"/><rect x="13" y="13" width="9" height="9" rx="2"/></svg>
          </button>
          <button :class="['gl-vt-btn', { active: viewMode === 'table' }]" @click="viewMode = 'table'" title="Table view">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
        </div>
      </div>

      <!-- ── SKELETON ── -->
      <template v-if="loading">
        <div class="gl-cards-grid">
          <div v-for="i in 6" :key="i" class="gl-cat-card gl-skel-card" :style="`animation-delay:${i*40}ms`">
            <div class="gl-skel-img-sq"></div>
            <div class="gl-skel-body">
              <div class="gl-skel-line w60"></div>
              <div class="gl-skel-line w40"></div>
            </div>
            <div class="gl-skel-actions">
              <div class="gl-skel-line gl-skel-btn-flex"></div>
              <div class="gl-skel-line gl-skel-btn-sq"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- ── CARD VIEW ── -->
      <template v-else-if="viewMode === 'cards'">
        <div v-if="categories.length === 0" class="gl-empty">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
          <p>No categories yet.</p>
          <router-link to="/owner/categories/create" class="gl-add-btn" style="font-size:13px;padding:8px 16px">Create first category</router-link>
        </div>

        <div v-else class="gl-cards-grid">
          <div
            v-for="(cat, idx) in categories"
            :key="cat.id"
            class="gl-cat-card"
            :style="`animation-delay:${idx * 40}ms`"
          >
            <!-- Order badge -->
            <div class="gl-cat-order">#{{ cat.sort_order }}</div>

            <!-- Image -->
            <div class="gl-cat-img-wrap">
              <img v-if="cat.image && !imgErrors[cat.id]" :src="cat.image" class="gl-cat-img" :alt="cat.name" @error="imgErrors[cat.id] = true">
              <div v-else class="gl-cat-img-placeholder">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
              </div>
            </div>

            <!-- Info -->
            <div class="gl-cat-info">
              <div class="gl-cat-name">{{ cat.name }}</div>
              <div class="gl-cat-slug">{{ cat.slug }}</div>
              <div class="gl-cat-meta">
                <span class="gl-cat-count">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                  {{ cat.products_count || 0 }} {{ cat.products_count === 1 ? 'product' : 'products' }}
                </span>
                <span :class="['gl-status-badge', cat.is_active ? 'active' : 'inactive']">
                  <span class="gl-status-dot"></span>
                  {{ cat.is_active ? 'Active' : 'Inactive' }}
                </span>
              </div>
            </div>

            <!-- Actions -->
            <div class="gl-bc-actions">
              <router-link :to="`/owner/categories/${cat.id}/edit`" class="gl-bc-btn-edit">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Edit
              </router-link>
              <button @click="confirmDelete(cat)" class="gl-bc-btn-delete" title="Delete">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- ── TABLE VIEW ── -->
      <template v-else>
        <div class="gl-table-wrap">
          <table class="gl-table">
            <thead>
              <tr>
                <th style="width:60px">Order</th>
                <th>Category</th>
                <th>Products</th>
                <th>Status</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="categories.length === 0">
                <td colspan="5" class="gl-tbl-empty">
                  No categories found. <router-link to="/owner/categories/create">Create your first category</router-link>
                </td>
              </tr>
              <tr v-for="cat in categories" :key="cat.id" class="gl-tbl-row">
                <td>
                  <span class="gl-order-badge">#{{ cat.sort_order }}</span>
                </td>
                <td>
                  <div class="gl-tbl-cat">
                    <div class="gl-tbl-cat-img">
                      <img v-if="cat.image && !imgErrors[cat.id]" :src="cat.image" class="gl-cat-img-sm" :alt="cat.name" @error="imgErrors[cat.id] = true">
                      <div v-else class="gl-cat-placeholder-sm">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                      </div>
                    </div>
                    <div>
                      <div class="gl-tbl-cat-name">{{ cat.name }}</div>
                      <div class="gl-tbl-cat-slug">{{ cat.slug }}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="gl-prod-count">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                    {{ cat.products_count || 0 }}
                  </span>
                </td>
                <td>
                  <span :class="['gl-status-badge', cat.is_active ? 'active' : 'inactive']">
                    <span class="gl-status-dot"></span>
                    {{ cat.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>
                  <div class="gl-tbl-actions">
                    <router-link :to="`/owner/categories/${cat.id}/edit`" class="gl-tbl-btn-edit" title="Edit">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </router-link>
                    <button @click="confirmDelete(cat)" class="gl-tbl-btn-delete" title="Delete">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- Pagination -->
      <div v-if="!loading && pagination.last_page > 1" class="gl-pagination">
        <button class="gl-page-btn" :disabled="pagination.current_page === 1" @click="fetchCategories(pagination.current_page - 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <button v-for="page in pagination.last_page" :key="page" :class="['gl-page-num', { active: page === pagination.current_page }]" @click="fetchCategories(page)">{{ page }}</button>
        <button class="gl-page-btn" :disabled="pagination.current_page === pagination.last_page" @click="fetchCategories(pagination.current_page + 1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>

    </div>

    <!-- ── Mobile FAB ── -->
    <router-link to="/owner/categories/create" class="gl-mobile-fab">
      <span class="gl-fab-inner">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Add Category
      </span>
    </router-link>

    <!-- ── Delete Modal ── -->
    <div v-if="showDeleteModal" class="gl-modal-backdrop" @click.self="showDeleteModal = false">
      <div class="gl-modal">
        <div class="gl-modal-icon gl-modal-icon-danger">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
        </div>
        <h2 class="gl-modal-title">Delete Category</h2>
        <p class="gl-modal-body">
          Are you sure you want to delete <strong>{{ selectedCategory?.name }}</strong>?
        </p>
        <div v-if="selectedCategory?.products_count > 0" class="gl-modal-blocked">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <span>This category has <strong>{{ selectedCategory.products_count }}</strong> product(s). Please reassign them to another category or delete them first.</span>
        </div>
        <div class="gl-modal-actions">
          <button class="gl-modal-cancel" @click="showDeleteModal = false">Cancel</button>
          <button class="gl-modal-confirm" @click="deleteCategory" :disabled="deleting || selectedCategory?.products_count > 0">
            <span v-if="deleting" class="spinner-border spinner-border-sm" style="width:13px;height:13px;border-width:2px"></span>
            <span v-else>Delete</span>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ownerCategoryApi } from '@/services/ownerApi';

export default {
  name: 'CategoryList',

  data() {
    return {
      loading: true,
      deleting: false,
      viewMode: 'cards',
      showDeleteModal: false,
      imgErrors: {},
      categories: [],
      selectedCategory: null,
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
      }
    };
  },

  computed: {
    totalProducts() {
      return this.categories.reduce((sum, c) => sum + (c.products_count || 0), 0);
    }
  },

  mounted() {
    this.fetchCategories();
    if (window.innerWidth <= 767) this.viewMode = 'cards';
    this._onResize = () => { if (window.innerWidth <= 767) this.viewMode = 'cards'; };
    window.addEventListener('resize', this._onResize);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this._onResize);
  },

  methods: {
    async fetchCategories(page = 1) {
      try {
        this.loading = true;
        const response = await ownerCategoryApi.list({ page });
        this.categories = response.data.data;
        if (response.data.meta) {
          this.pagination = {
            current_page: response.data.meta.current_page,
            last_page: response.data.meta.last_page,
            per_page: response.data.meta.per_page,
            total: response.data.meta.total
          };
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        this.loading = false;
      }
    },

    confirmDelete(category) {
      this.selectedCategory = category;
      this.showDeleteModal = true;
    },

    async deleteCategory() {
      if (!this.selectedCategory) return;
      try {
        this.deleting = true;
        await ownerCategoryApi.delete(this.selectedCategory.id);
        this.showDeleteModal = false;
        this.fetchCategories(this.pagination.current_page);
      } catch (error) {
        console.error('Failed to delete category:', error);
        alert(error.response?.data?.message || 'Failed to delete category');
      } finally {
        this.deleting = false;
        this.selectedCategory = null;
      }
    }
  }
};
</script>

<style scoped>
.gl-categories-page { padding-bottom: 48px; }

/* ── Header ── */
.gl-branch-header {
  display: flex; align-items: flex-end; justify-content: space-between;
  padding: 28px 0 20px;
}
.gl-branch-breadcrumb {
  display: flex; align-items: center; gap: 6px;
  font-size: 12.5px; color: #9ca3af; margin-bottom: 4px;
}
.gl-branch-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-branch-breadcrumb a:hover { color: #900C27; }
.gl-bc-sep { opacity: 0.5; }
.gl-branch-title { font-size: 22px; font-weight: 700; color: #111318; letter-spacing: -0.4px; margin: 0; }
.gl-add-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px; border-radius: 10px;
  background: #900C27; color: #fff;
  font-size: 13.5px; font-weight: 600; text-decoration: none;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 2px 8px rgba(144,12,39,0.25);
}
.gl-add-btn:hover { background: #6f0a1e; color: #fff; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(144,12,39,0.30); }

/* ── Stats strip ── */
.gl-stats-strip {
  display: flex; align-items: center; gap: 14px;
  background: #fff; border: 1.5px solid #f0f1f3;
  border-radius: 14px; padding: 12px 18px; margin-bottom: 16px;
}
.gl-stat-chip { display: flex; flex-direction: column; align-items: center; gap: 1px; min-width: 44px; }
.gl-stat-num { font-size: 18px; font-weight: 700; color: #111318; line-height: 1.1; }
.gl-stat-num.gl-stat-active { color: #900C27; }
.gl-stat-num.gl-stat-inactive { color: #9ca3af; }
.gl-stat-lbl { font-size: 10.5px; color: #9ca3af; font-weight: 500; letter-spacing: 0.3px; }
.gl-stat-divider { width: 1px; height: 28px; background: #f0f1f3; flex-shrink: 0; }
.gl-stat-spacer { flex: 1; }
.gl-view-toggle { display: flex; gap: 3px; background: #f3f4f6; border-radius: 8px; padding: 3px; }
.gl-vt-btn {
  width: 28px; height: 28px; border-radius: 6px; border: none;
  background: transparent; color: #9ca3af; cursor: pointer;
  display: flex; align-items: center; justify-content: center; transition: all 0.15s;
}
.gl-vt-btn.active { background: #fff; color: #900C27; box-shadow: 0 1px 4px rgba(0,0,0,0.10); }
.gl-vt-btn:hover:not(.active) { color: #374151; }

/* ── Empty ── */
.gl-empty {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  padding: 64px 24px; color: #9ca3af;
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px;
}
.gl-empty svg { color: #d1d5db; }
.gl-empty p { font-size: 14px; margin: 0; }

/* ── Cards grid ── */
.gl-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px; margin-bottom: 20px;
}

/* ── Category card ── */
.gl-cat-card {
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px;
  overflow: hidden; display: flex; flex-direction: column;
  position: relative;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;
  animation: gl-card-in 0.35s ease both;
}
@keyframes gl-card-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
.gl-cat-card:hover {
  border-color: rgba(144,12,39,0.25);
  box-shadow: 0 8px 24px rgba(144,12,39,0.10);
  transform: translateY(-2px);
}

/* Order badge */
.gl-cat-order {
  position: absolute; top: 10px; left: 10px; z-index: 2;
  font-size: 10px; font-weight: 700;
  background: rgba(0,0,0,0.45); color: #fff;
  padding: 2px 7px; border-radius: 20px;
  backdrop-filter: blur(4px);
}

/* Image */
.gl-cat-img-wrap { width: 100%; height: 150px; overflow: hidden; flex-shrink: 0; }
.gl-cat-img { width: 100%; height: 100%; object-fit: cover; }
.gl-cat-img-placeholder {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #f0faf8, #e0f2ee);
  color: rgba(144,12,39,0.35);
}

/* Info */
.gl-cat-info { padding: 12px 14px 10px; flex: 1; }
.gl-cat-name { font-size: 14px; font-weight: 700; color: #111318; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gl-cat-slug { font-size: 11px; color: #9ca3af; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gl-cat-meta { display: flex; align-items: center; justify-content: space-between; margin-top: 8px; }
.gl-cat-count {
  display: flex; align-items: center; gap: 4px;
  font-size: 11.5px; color: #6b7280; font-weight: 500;
}
.gl-cat-count svg { color: #9ca3af; }

/* Status badge */
.gl-status-badge {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 20px;
}
.gl-status-badge.active { background: rgba(144,12,39,0.10); color: #900C27; }
.gl-status-badge.inactive { background: #f3f4f6; color: #9ca3af; }
.gl-status-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
.gl-status-badge.active .gl-status-dot { animation: gl-dot-pulse 2s ease infinite; }
@keyframes gl-dot-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(144,12,39,0.4); }
  70%  { box-shadow: 0 0 0 5px rgba(144,12,39,0); }
  100% { box-shadow: 0 0 0 0 rgba(144,12,39,0); }
}

/* Card actions */
.gl-bc-actions {
  display: flex; gap: 6px; padding: 10px 14px 14px;
  border-top: 1px solid #f3f4f6;
}
.gl-bc-btn-edit, .gl-bc-btn-delete {
  display: inline-flex; align-items: center; justify-content: center; gap: 5px;
  height: 30px; border-radius: 7px; font-size: 12px; font-weight: 600;
  cursor: pointer; border: none; text-decoration: none; transition: all 0.15s;
}
.gl-bc-btn-edit { flex: 1; background: rgba(144,12,39,0.08); color: #900C27; }
.gl-bc-btn-edit:hover { background: rgba(144,12,39,0.15); }
.gl-bc-btn-delete { flex: 0 0 30px; width: 30px; background: rgba(239,68,68,0.07); color: #ef4444; }
.gl-bc-btn-delete:hover { background: rgba(239,68,68,0.14); }

/* ── Skeleton ── */
.gl-skel-actions { display: flex; gap: 6px; padding: 10px 14px 14px; border-top: 1px solid #f3f4f6; }
.gl-skel-btn-flex { flex: 1; height: 30px; border-radius: 7px; margin: 0; }
.gl-skel-btn-sq { flex: 0 0 30px; width: 30px; height: 30px; border-radius: 7px; margin: 0; }
.gl-skel-card { cursor: default; pointer-events: none; }
.gl-skel-img-sq {
  width: 100%; height: 120px;
  background: linear-gradient(90deg, #f0f1f3 25%, #e8e9eb 50%, #f0f1f3 75%);
  background-size: 200% 100%; animation: gl-skel-wave 1.5s ease infinite;
}
@keyframes gl-skel-wave { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.gl-skel-body { padding: 14px; display: flex; flex-direction: column; gap: 8px; }
.gl-skel-line {
  height: 10px; border-radius: 6px;
  background: linear-gradient(90deg, #f0f1f3 25%, #e8e9eb 50%, #f0f1f3 75%);
  background-size: 200% 100%; animation: gl-skel-wave 1.5s ease infinite;
}
.gl-skel-line.w60 { width: 60%; }
.gl-skel-line.w40 { width: 40%; }

/* ── Table view ── */
.gl-table-wrap {
  background: #fff; border: 1.5px solid #f0f1f3;
  border-radius: 16px; overflow: hidden; margin-bottom: 20px;
}
.gl-table { width: 100%; border-collapse: collapse; }
.gl-table thead tr { border-bottom: 1.5px solid #f0f1f3; }
.gl-table th {
  padding: 12px 16px; font-size: 11px; font-weight: 700;
  color: #9ca3af; text-transform: uppercase; letter-spacing: 0.6px;
  text-align: left; white-space: nowrap;
}
.gl-table th.text-center { text-align: center; }
.gl-tbl-row { border-bottom: 1px solid #f8f9fa; transition: background 0.12s; }
.gl-tbl-row:last-child { border-bottom: none; }
.gl-tbl-row:hover { background: #fafef9; }
.gl-table td { padding: 13px 16px; vertical-align: middle; }
.gl-tbl-empty { text-align: center; color: #9ca3af; padding: 40px !important; font-size: 14px; }
.gl-tbl-empty a { color: #900C27; font-weight: 600; }

.gl-order-badge {
  font-size: 11.5px; font-weight: 700; color: #6b7280;
  background: #f3f4f6; padding: 3px 10px; border-radius: 20px;
}
.gl-tbl-cat { display: flex; align-items: center; gap: 12px; }
.gl-cat-img-sm { width: 42px; height: 42px; border-radius: 10px; object-fit: cover; }
.gl-cat-placeholder-sm {
  width: 42px; height: 42px; border-radius: 10px; flex-shrink: 0;
  background: linear-gradient(135deg, #f0faf8, #e0f2ee);
  color: rgba(144,12,39,0.4);
  display: flex; align-items: center; justify-content: center;
}
.gl-tbl-cat-name { font-size: 13.5px; font-weight: 600; color: #111318; }
.gl-tbl-cat-slug { font-size: 11.5px; color: #9ca3af; margin-top: 2px; }
.gl-prod-count {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 12px; font-weight: 600; color: #6b7280;
  background: #f3f4f6; padding: 3px 10px; border-radius: 20px;
}
.gl-prod-count svg { color: #9ca3af; }

.gl-tbl-actions { display: flex; align-items: center; justify-content: center; gap: 6px; }
.gl-tbl-btn-edit, .gl-tbl-btn-delete {
  width: 32px; height: 32px; border-radius: 8px; border: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; text-decoration: none; transition: all 0.15s;
}
.gl-tbl-btn-edit { background: rgba(144,12,39,0.08); color: #900C27; }
.gl-tbl-btn-edit:hover { background: rgba(144,12,39,0.16); }
.gl-tbl-btn-delete { background: rgba(239,68,68,0.07); color: #ef4444; }
.gl-tbl-btn-delete:hover { background: rgba(239,68,68,0.14); }

/* ── Pagination ── */
.gl-pagination { display: flex; align-items: center; justify-content: center; gap: 4px; margin-top: 8px; }
.gl-page-btn {
  width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid #e5e7eb;
  background: #fff; color: #6b7280; cursor: pointer;
  display: flex; align-items: center; justify-content: center; transition: all 0.15s;
}
.gl-page-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; }
.gl-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.gl-page-num {
  min-width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid #e5e7eb;
  background: #fff; color: #374151; font-size: 13px; font-weight: 600;
  cursor: pointer; padding: 0 8px; transition: all 0.15s;
}
.gl-page-num:hover { border-color: #900C27; color: #900C27; }
.gl-page-num.active { background: #900C27; border-color: #900C27; color: #fff; }

/* ── Delete Modal ── */
.gl-modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,0.35);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; backdrop-filter: blur(4px);
  animation: gl-backdrop-in 0.15s ease;
}
@keyframes gl-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
.gl-modal {
  background: #fff; border-radius: 20px; padding: 32px 28px;
  width: 100%; max-width: 400px; margin: 16px; text-align: center;
  animation: gl-modal-in 0.2s cubic-bezier(0.34,1.56,0.64,1);
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
}
@keyframes gl-modal-in {
  from { opacity: 0; transform: scale(0.92) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
.gl-modal-icon {
  width: 52px; height: 52px; border-radius: 16px; margin: 0 auto 16px;
  display: flex; align-items: center; justify-content: center;
}
.gl-modal-icon-danger { background: rgba(239,68,68,0.10); color: #ef4444; }
.gl-modal-title { font-size: 17px; font-weight: 700; color: #111318; margin: 0 0 8px; }
.gl-modal-body { font-size: 13.5px; color: #6b7280; margin: 0 0 16px; line-height: 1.5; }
.gl-modal-body strong { color: #111318; }
.gl-modal-blocked {
  display: flex; align-items: flex-start; gap: 8px; text-align: left;
  background: #fff8ed; border: 1px solid #fde68a; border-radius: 10px;
  padding: 12px 14px; margin-bottom: 20px;
  font-size: 13px; color: #92400e; line-height: 1.5;
  flex-shrink: 0;
}
.gl-modal-blocked svg { flex-shrink: 0; margin-top: 1px; color: #f59e0b; }
.gl-modal-blocked strong { color: #92400e; }
.gl-modal-actions { display: flex; gap: 10px; }
.gl-modal-cancel {
  flex: 1; height: 40px; border-radius: 10px;
  border: 1.5px solid #e5e7eb; background: #fff;
  color: #6b7280; font-size: 13.5px; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
}
.gl-modal-cancel:hover { background: #f9fafb; border-color: #d1d5db; color: #374151; }
.gl-modal-confirm {
  flex: 1; height: 40px; border-radius: 10px; border: none;
  background: #ef4444; color: #fff;
  font-size: 13.5px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  transition: background 0.15s;
}
.gl-modal-confirm:hover:not(:disabled) { background: #dc2626; }
.gl-modal-confirm:disabled { opacity: 0.65; cursor: not-allowed; }

/* ── Mobile FAB ── */
.gl-mobile-fab {
  display: none;
  position: fixed; bottom: 24px; right: 20px; z-index: 200;
  background: transparent; border: none; box-shadow: none;
  text-decoration: none;
}
.gl-fab-inner {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 13px 20px; border-radius: 50px;
  background: #900C27; color: #fff;
  font-size: 14px; font-weight: 700;
  box-shadow: 0 6px 24px rgba(144,12,39,0.40), 0 2px 6px rgba(0,0,0,0.15);
  transition: transform 0.15s, box-shadow 0.15s;
}
.gl-mobile-fab:hover .gl-fab-inner { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(144,12,39,0.45); }

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  .gl-categories-page { padding-bottom: 100px; }
  .gl-branch-header { padding: 14px 0 12px; }
  .gl-branch-title { font-size: 18px; }
  .gl-header-add-btn { display: none !important; }
  .gl-view-toggle { display: none !important; }
  .gl-mobile-fab { display: flex; }

  .gl-stats-strip { gap: 10px; padding: 10px 14px; }
  .gl-stat-chip { min-width: 36px; }
  .gl-stat-num { font-size: 16px; }

  .gl-cards-grid { grid-template-columns: 1fr !important; gap: 10px; }

  /* Bigger tap targets on action buttons */
  .gl-bc-btn-edit,
  .gl-bc-btn-delete { height: 38px !important; }
  .gl-bc-btn-delete { flex: 0 0 38px !important; width: 38px !important; }

  /* Modal — bottom sheet, clear of navbar */
  .gl-modal-backdrop { padding: 68px 16px 16px !important; align-items: center !important; }
  .gl-modal { border-radius: 16px !important; max-height: calc(100vh - 84px) !important; padding: 24px 20px; }
}
</style>

<style>
/* ── Dark mode ── */
body.dark-only .gl-branch-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-branch-breadcrumb,
body.dark-only .gl-branch-breadcrumb a { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-branch-breadcrumb a:hover { color: #900C27 !important; }

body.dark-only .gl-stats-strip { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-stat-num { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-stat-lbl { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-stat-divider { background: #3d4255 !important; }
body.dark-only .gl-view-toggle { background: #323644 !important; }
body.dark-only .gl-vt-btn.active { background: #3d4255 !important; box-shadow: none !important; }
body.dark-only .gl-vt-btn:hover:not(.active) { color: rgba(255,255,255,0.6) !important; }

body.dark-only .gl-cat-card { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-cat-card:hover { border-color: rgba(144,12,39,0.35) !important; box-shadow: 0 8px 24px rgba(0,0,0,0.3) !important; }
body.dark-only .gl-cat-img-placeholder { background: linear-gradient(135deg, #2a3340, #243330) !important; }
body.dark-only .gl-cat-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-cat-slug { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-cat-count { color: rgba(255,255,255,0.4) !important; }
body.dark-only .gl-status-badge.inactive { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.35) !important; }
body.dark-only .gl-bc-actions { border-top-color: #323644 !important; }
body.dark-only .gl-bc-btn-edit { background: rgba(144,12,39,0.12) !important; }
body.dark-only .gl-bc-btn-delete { background: rgba(239,68,68,0.10) !important; }

body.dark-only .gl-table-wrap { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-table th { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-table thead tr { border-bottom-color: #323644 !important; }
body.dark-only .gl-tbl-row { border-bottom-color: #2d3044 !important; }
body.dark-only .gl-tbl-row:hover { background: rgba(144,12,39,0.06) !important; }
body.dark-only .gl-cat-placeholder-sm { background: linear-gradient(135deg, #2a3340, #243330) !important; }
body.dark-only .gl-tbl-cat-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-tbl-cat-slug { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-order-badge { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.45) !important; }
body.dark-only .gl-prod-count { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.45) !important; }
body.dark-only .gl-tbl-btn-edit { background: rgba(144,12,39,0.12) !important; }
body.dark-only .gl-tbl-btn-delete { background: rgba(239,68,68,0.10) !important; }

body.dark-only .gl-page-btn,
body.dark-only .gl-page-num { background: #262932 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-page-num.active { background: #900C27 !important; border-color: #900C27 !important; color: #fff !important; }

body.dark-only .gl-modal { background: #262932 !important; }
body.dark-only .gl-modal-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-modal-body { color: rgba(255,255,255,0.5) !important; }
body.dark-only .gl-modal-body strong { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-modal-blocked { background: rgba(245,158,11,0.10) !important; border-color: rgba(245,158,11,0.25) !important; color: #fbbf24 !important; }
body.dark-only .gl-modal-blocked strong { color: #fbbf24 !important; }
body.dark-only .gl-modal-cancel { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-modal-cancel:hover { background: #3d4255 !important; color: rgba(255,255,255,0.75) !important; }

body.dark-only .gl-empty { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-empty p { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-skel-img-sq,
body.dark-only .gl-skel-line { background: linear-gradient(90deg, #2d3044 25%, #323644 50%, #2d3044 75%) !important; background-size: 200% 100% !important; }
body.dark-only .gl-skel-actions { border-top-color: #2d3044 !important; }

.gl-mobile-fab { background: transparent !important; border: none !important; box-shadow: none !important; }
body.dark-only .gl-mobile-fab { background: transparent !important; box-shadow: none !important; }
body.dark-only .gl-fab-inner { box-shadow: 0 6px 24px rgba(144,12,39,0.35), 0 2px 6px rgba(0,0,0,0.30) !important; }
</style>
